(() => {
  const STORAGE_KEY = 'recentlyViewedProducts';
  const MAX_STORED_PRODUCTS = 20;
  const SECTION_SELECTOR = '[data-recently-viewed-section]';
  const PRODUCT_PATH_PATTERN = /\/products\/([^/?#]+)/;

  const getStoredHandles = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item.handle === 'string') return item.handle;
          if (item && typeof item.url === 'string') {
            const match = item.url.match(/\/products\/([^/?#]+)/);
            return match ? decodeURIComponent(match[1]) : null;
          }

          return null;
        })
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  const storeHandles = (handles) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED_PRODUCTS)));
    } catch (error) {
      // Ignore storage write failures.
    }
  };

  const getCurrentProductHandle = () => {
    const match = window.location.pathname.match(PRODUCT_PATH_PATTERN);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const trackCurrentProduct = () => {
    const handle = getCurrentProductHandle();
    if (!handle) return;

    const nextHandles = [handle, ...getStoredHandles().filter((item) => item !== handle)];
    storeHandles(nextHandles);
  };

  const getRootUrl = (section) => {
    if (section && section.dataset.rootUrl) return section.dataset.rootUrl;
    return '/';
  };

  const buildProductUrl = (handle, section) => {
    const rootUrl = getRootUrl(section).replace(/\/?$/, '/');
    return `${rootUrl}products/${encodeURIComponent(handle)}`;
  };

  const getCardSectionId = (section) => {
    return section.dataset.enableQuickAdd === 'false'
      ? 'recently-viewed-product-card-no-quick-add'
      : 'recently-viewed-product-card';
  };

  const fetchProductCard = async (handle, section) => {
    const response = await fetch(
      `${buildProductUrl(handle, section)}?section_id=${getCardSectionId(section)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to load product card: ${handle}`);
    }

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const card = parsed.querySelector('[data-recently-viewed-card]');

    return card ? card.outerHTML : '';
  };

  const bindSlider = (section) => {
    const shell = section.querySelector('slider-component');
    const slider = section.querySelector('[id^="Slider-"]');
    if (!shell || !slider) return;

    const slides = slider.querySelectorAll('.featured-collection-showcase__slide');
    slides.forEach((slide, index) => {
      slide.id = `Slide-${section.dataset.sectionId}-${index + 1}`;
    });

    const footerEl = section.querySelector('.featured-collection-showcase__footer');
    if (footerEl) footerEl.hidden = slides.length <= 1;

    const start = () => {
      if (shell.resetPages) shell.resetPages();

      const bar = section.querySelector('.featured-collection-showcase__scrollbar');
      const thumb = section.querySelector('.featured-collection-showcase__scrollbar-thumb');

      const equalize = () => {
        const currentSlides = slider.querySelectorAll('.featured-collection-showcase__slide');
        let max = 0;
        currentSlides.forEach((slide) => { slide.style.height = 'auto'; });
        currentSlides.forEach((slide) => { max = Math.max(max, slide.offsetHeight); });
        if (!max || window.innerWidth < 750) return;
        currentSlides.forEach((slide) => { slide.style.height = `${max}px`; });
      };

      const maxScroll = () => Math.max(0, slider.scrollWidth - slider.clientWidth);

      const updateBar = () => {
        if (!bar || !thumb) return;
        const max = maxScroll();
        const visible = slider.scrollWidth > 0 ? slider.clientWidth / slider.scrollWidth : 1;
        const thumbRatio = Math.min(1, Math.max(visible, 0.08));
        const progress = max === 0 ? 0 : Math.min(1, Math.abs(slider.scrollLeft) / max);
        thumb.style.width = `${thumbRatio * 100}%`;
        thumb.style.insetInlineStart = `${progress * (1 - thumbRatio) * 100}%`;
        bar.hidden = max === 0;
      };

      if (section.dataset.recentSliderReady !== 'true') {
        section.dataset.recentSliderReady = 'true';

        if (bar && thumb) {
          let dragging = false;
          slider.addEventListener('scroll', updateBar, { passive: true });
          bar.addEventListener('pointerdown', (event) => {
            dragging = true;
            if (bar.setPointerCapture) {
              try { bar.setPointerCapture(event.pointerId); } catch (error) {}
            }
            const rect = bar.getBoundingClientRect();
            const rtl = document.documentElement.getAttribute('dir') === 'rtl';
            const thumbWidth = thumb.getBoundingClientRect().width;
            const usable = Math.max(1, rect.width - thumbWidth);
            const x = rtl ? rect.right - event.clientX - thumbWidth / 2 : event.clientX - rect.left - thumbWidth / 2;
            const progress = Math.min(1, Math.max(0, x / usable));
            slider.scrollLeft = (rtl ? -1 : 1) * progress * maxScroll();
          });
          bar.addEventListener('pointermove', (event) => {
            if (!dragging) return;
            const rect = bar.getBoundingClientRect();
            const rtl = document.documentElement.getAttribute('dir') === 'rtl';
            const thumbWidth = thumb.getBoundingClientRect().width;
            const usable = Math.max(1, rect.width - thumbWidth);
            const x = rtl ? rect.right - event.clientX - thumbWidth / 2 : event.clientX - rect.left - thumbWidth / 2;
            const progress = Math.min(1, Math.max(0, x / usable));
            slider.scrollLeft = (rtl ? -1 : 1) * progress * maxScroll();
          });
          const stopDrag = () => { dragging = false; };
          bar.addEventListener('pointerup', stopDrag);
          bar.addEventListener('pointercancel', stopDrag);
        }

        slider.addEventListener('scroll', () => {
          const max = maxScroll();
          const next = shell.querySelector('[name="next"]');
          const prev = shell.querySelector('[name="previous"]');
          if (next && Math.abs(slider.scrollLeft) >= max - 2) next.setAttribute('disabled', 'disabled');
          if (prev && Math.abs(slider.scrollLeft) <= 2) prev.setAttribute('disabled', 'disabled');
        }, { passive: true });

        if (window.ResizeObserver) {
          const observer = new ResizeObserver(() => {
            equalize();
            updateBar();
          });
          observer.observe(slider);
        }
      }

      slider.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', equalize, { once: true });
      });

      equalize();
      updateBar();
    };

    if (window.customElements.get('slider-component')) {
      start();
    } else {
      window.customElements.whenDefined('slider-component').then(start);
    }
  };

  const renderEmptyState = (section) => {
    const trackEl = section.querySelector('.featured-collection-showcase__track');
    const emptyEl = section.querySelector('.featured-collection-showcase__empty');
    const footerEl = section.querySelector('.featured-collection-showcase__footer');
    if (trackEl) {
      trackEl.innerHTML = '';
    }

    if (footerEl) {
      footerEl.hidden = true;
    }

    if (emptyEl) {
      emptyEl.hidden = false;
      emptyEl.textContent = section.dataset.emptyMessage || emptyEl.textContent;
    }
  };

  const hideSection = (section) => {
    renderEmptyState(section);
    section.hidden = true;
  };

  const initSection = async (section) => {
    const trackEl = section.querySelector('.featured-collection-showcase__track');
    const emptyEl = section.querySelector('.featured-collection-showcase__empty');
    if (!trackEl) return;

    section.hidden = false;

    if (emptyEl) {
      emptyEl.hidden = true;
    }

    const limit = Number(section.dataset.limit || 8);
    const currentHandle = getCurrentProductHandle();
    const handles = getStoredHandles()
      .filter((handle) => handle && handle !== currentHandle)
      .slice(0, limit);

    if (!handles.length) {
      hideSection(section);
      return;
    }

    try {
      const cards = await Promise.all(
        handles.map((handle) =>
          fetchProductCard(handle, section).catch(() => '')
        )
      );

      const visibleCards = cards
        .map((card) => card.trim())
        .filter(Boolean);

      if (!visibleCards.length) {
        hideSection(section);
        return;
      }

      section.hidden = false;
      trackEl.innerHTML = visibleCards.join('');
      bindSlider(section);
    } catch (error) {
      hideSection(section);
    }
  };

  const initSections = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      initSection(section);
    });
  };

  const boot = () => {
    trackCurrentProduct();
    initSections();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target && event.target.matches && event.target.matches(SECTION_SELECTOR)
      ? event.target
      : event.target && event.target.querySelector
        ? event.target.querySelector(SECTION_SELECTOR)
        : null;

    if (section) {
      initSection(section);
    }
  });
})();
